import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {
  ACTIVE_RUN, downloadRunLog, errorText, fetchRun, useCancelSync,
  type RunDetail, type RunEvent,
} from '@/api/hrms-sync';
import { fmtNum, fmtWhen } from './format';
import { ImportChip, RunStatusChip, SourceStatusIcon } from './shared';

const POLL_MS = 2000;
const MAX_EVENTS = 3000;

/**
 * Live view of one run: overall progress, each source's status and counts,
 * and every event as it is written — errors included, while the run carries
 * on with the remaining sources.
 */
export function RunProgressPanel({
  runId, canEdit, kindLabel,
}: {
  runId: string;
  canEdit: boolean;
  kindLabel: (key: string | null | undefined) => string;
}) {
  const qc = useQueryClient();
  const [run, setRun] = useState<RunDetail | null>(null);
  const [events, setEvents] = useState<RunEvent[]>([]);
  const [pollError, setPollError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'problems'>('all');
  const [logError, setLogError] = useState<string | null>(null);
  const cancel = useCancelSync();
  const lastEvent = useRef(0);
  const logBox = useRef<HTMLDivElement | null>(null);
  const stick = useRef(true);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    lastEvent.current = 0;
    setEvents([]);
    setRun(null);

    const poll = async () => {
      try {
        const data = await fetchRun(runId, lastEvent.current);
        if (cancelled) return;
        setRun(data);
        setPollError(null);
        if (data.events.length) {
          lastEvent.current = data.last_event_id;
          setEvents((prev) => {
            const next = prev.concat(data.events);
            return next.length > MAX_EVENTS ? next.slice(next.length - MAX_EVENTS) : next;
          });
        }
        const active = ACTIVE_RUN.includes(data.status);
        // Drain the event backlog fast; otherwise poll at the normal pace.
        const more = data.events.length >= 500;
        if (active || more) {
          timer = setTimeout(poll, more ? 50 : POLL_MS);
        } else {
          void qc.invalidateQueries({ queryKey: ['hrms-sync'] });
          // A theme import re-brands the entities: repaint the sidebar and theme now.
          if (data.sources.some((s) => s.source_key === 'core:theme' && s.promote_status === 'IMPORTED')) {
            void qc.invalidateQueries({ queryKey: ['entity-branding'] });
          }
        }
      } catch (err) {
        if (cancelled) return;
        setPollError(errorText(err));
        timer = setTimeout(poll, POLL_MS * 3);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId, qc]);

  useEffect(() => {
    const el = logBox.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [events, filter]);

  const shown = useMemo(
    () => (filter === 'all' ? events : events.filter((e) => e.level !== 'INFO')),
    [events, filter],
  );
  const titleOf = useMemo(() => {
    const m: Record<string, string> = {};
    for (const s of run?.sources ?? []) m[s.source_key] = s.title;
    return m;
  }, [run]);

  if (!run) {
    return (
      <Box sx={{ py: 3 }}>
        <LinearProgress />
        {pollError && <Alert severity="warning" sx={{ mt: 2 }}>{pollError}</Alert>}
      </Box>
    );
  }

  const active = ACTIVE_RUN.includes(run.status);
  const total = Math.max(run.sources_total, 1);
  const pct = Math.round((run.sources_done * 100) / total);
  const problems = events.filter((e) => e.level !== 'INFO').length;

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <RunStatusChip status={run.status} />
        <Typography variant="body2" color="text.secondary">
          {run.trigger.toLowerCase()} · started {fmtWhen(run.started_at || run.created_at)}
          {run.triggered_by ? ` by ${run.triggered_by}` : ''}
          {run.finished_at ? ` · finished ${fmtWhen(run.finished_at)}` : ''}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {run.error_count > 0 && <Chip size="small" color="error" label={`${run.error_count} error(s)`} />}
        {run.warning_count > 0 && <Chip size="small" color="warning" variant="outlined" label={`${run.warning_count} warning(s)`} />}
        {run.has_log_file && canEdit && (
          <Button size="small" startIcon={<DownloadIcon />}
            onClick={() => downloadRunLog(run.id).catch((e) => setLogError(errorText(e)))}>
            Log file
          </Button>
        )}
        {active && canEdit && (
          <Button size="small" color="warning" startIcon={<StopCircleIcon />}
            disabled={run.status === 'CANCELLING' || cancel.isPending}
            onClick={() => cancel.mutate(run.id)}>
            {run.status === 'CANCELLING' ? 'Stopping…' : 'Stop'}
          </Button>
        )}
      </Stack>

      <LinearProgress variant={active && run.sources_total === 0 ? 'indeterminate' : 'determinate'}
        value={pct} color={run.status === 'FAILED' ? 'error' : run.error_count ? 'warning' : 'primary'}
        sx={{ height: 8, borderRadius: 4 }} />
      <Typography variant="caption" color="text.secondary">
        {run.sources_done} of {run.sources_total} source(s) done
        {run.sources_failed ? ` · ${run.sources_failed} failed` : ''}
      </Typography>

      {run.summary && !active && (
        <Alert severity={run.status === 'COMPLETED' ? 'success' : run.status === 'FAILED' ? 'error' : 'warning'}
          sx={{ mt: 1.5 }}>
          {run.summary}
          {run.status === 'COMPLETED_WITH_ERRORS' && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Everything that could be fetched was stored and imported. Fix the sources marked
              in red (or ask the uKnowva administrator to), then sync them again.
            </Typography>
          )}
        </Alert>
      )}
      {pollError && <Alert severity="warning" sx={{ mt: 1.5 }}>Lost contact — retrying. ({pollError})</Alert>}
      {cancel.error && <Alert severity="error" sx={{ mt: 1.5 }}>{errorText(cancel.error)}</Alert>}
      {logError && <Alert severity="error" sx={{ mt: 1.5 }}>{logError}</Alert>}

      <Paper variant="outlined" sx={{ mt: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ '& td, & th': { py: 0.6 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 28 }} />
              <TableCell>Source</TableCell>
              <TableCell align="right">Fetched</TableCell>
              <TableCell align="right">New / changed</TableCell>
              <TableCell>Payroll import</TableCell>
              <TableCell>Problem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {run.sources.map((s) => {
              const p = s.promote || {};
              const imported = (p.inserted ?? 0) + (p.updated ?? 0);
              return (
                <TableRow key={s.source_key} hover
                  sx={s.status === 'FAILED' ? { bgcolor: 'rgba(211,47,47,0.04)' } : undefined}>
                  <TableCell><SourceStatusIcon status={s.status} /></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{s.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {s.source_key}{s.pages ? ` · ${s.pages} page(s)` : ''}
                      {s.seconds != null ? ` · ${s.seconds}s` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {fmtNum(s.fetched)}{s.total != null && s.status === 'RUNNING' ? ` / ${fmtNum(s.total)}` : ''}
                  </TableCell>
                  <TableCell align="right">
                    {fmtNum(s.inserted)} / {fmtNum(s.updated)}
                    {s.withdrawn ? (
                      <Tooltip title="No longer returned by uKnowva — kept and flagged, never deleted">
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                          {s.withdrawn} withdrawn
                        </Typography>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.25} alignItems="flex-start">
                      <ImportChip status={s.promote_status} />
                      {s.promote_status === 'IMPORTED' && p.kind && (
                        <Typography variant="caption" color="text.secondary">
                          {kindLabel(p.kind)}: {imported} saved
                          {p.skipped ? `, ${p.skipped} skipped` : ''}
                        </Typography>
                      )}
                      {s.promote_status === 'NEEDS_MAPPING' && (
                        <Typography variant="caption" color="warning.main">
                          Map the columns to import as {kindLabel(p.kind)}
                        </Typography>
                      )}
                      {/* Attendance: "Fetched" counts what uKnowva sent; this is
                          what reached the Attendance page, and why the rest did not.
                          Branding: which entity took which part of the theme. */}
                      {(p.kind === 'attendance' || p.kind === 'branding') && p.note && (
                        <Typography variant="caption"
                          color={p.skipped ? 'warning.main' : 'text.secondary'}
                          sx={{ maxWidth: 360, wordBreak: 'break-word' }}>
                          {p.window ? `${p.window.replace('..', ' → ')}: ` : ''}{p.note}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="caption" color="error" sx={{ wordBreak: 'break-word' }}>
                      {s.error || p.error || ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            {run.sources.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    {active ? 'Discovering what uKnowva exposes…' : 'No sources were synced.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Stack direction="row" alignItems="center" sx={{ mt: 2, mb: 0.5 }}>
        <Typography variant="subtitle2">Activity</Typography>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup size="small" exclusive value={filter}
          onChange={(_e, v) => v && setFilter(v)}>
          <ToggleButton value="all">All ({events.length})</ToggleButton>
          <ToggleButton value="problems">Warnings & errors ({problems})</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box
        ref={logBox}
        onScroll={(e) => {
          const el = e.currentTarget;
          stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
        }}
        sx={{
          maxHeight: 280, overflowY: 'auto', fontFamily: 'monospace', fontSize: 12,
          bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 1,
        }}
      >
        {shown.length === 0 && (
          <Typography variant="caption" color="text.secondary">Nothing to show yet.</Typography>
        )}
        {shown.map((e) => (
          <Box key={e.id} sx={{
            py: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            color: e.level === 'ERROR' ? 'error.main' : e.level === 'WARNING' ? 'warning.dark' : 'text.primary',
          }}>
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {e.at ? new Date(e.at).toLocaleTimeString() : ''}{' '}
            </Box>
            <b>{e.level === 'INFO' ? '' : `${e.level} `}</b>
            {e.source_key ? `[${titleOf[e.source_key] ?? e.source_key}] ` : ''}
            {e.message}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
